import React, { useState } from 'react';
import MediaQuery from 'react-responsive';
import { t } from 'i18next';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import dayjsUtils from '@date-io/dayjs';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import Step from '../../components/step';
import Input from '../../components/input';
import ActionFooter from '../../components/action-footer';
import styles from './index.module.scss';

const useStyles = makeStyles({
  birthday: {
    '& .MuiInput-underline:after': {
      borderBottom: '2px solid #08F',
    },
    '& .MuiInput-underline:before': {
      borderBottom: '2px solid #DDD',
    },
  },
});

export default function UpdateDetails(props) {
  const [initialFocus, setInitialFocus] = useState(false);
  const [firstNameRef, setFirstNameRef] = useState(undefined);
  const [lastNameRef, setLastNameRef] = useState(undefined);
  const [passwordRef, setPasswordRef] = useState(undefined);
  const [confirmPasswordRef, setConfirmPasswordRef] = useState(undefined);
  const [firstName, setFirstName] = useState(undefined);
  const [lastName, setLastName] = useState(undefined);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstNameError, setFirstNameError] = useState(undefined);
  const [lastNameError, setLastNameError] = useState(undefined);
  const [passwordError, setPasswordError] = useState(undefined);
  const [confirmPasswordError, setConfirmPasswordError] = useState(undefined);
  const [birthday, setBirthday] = useState(null);
  const [birthdayError, setBirthdayError] = useState(undefined);
  const {
    step,
    currentStep,
    logo,
    skipName,
    skipPassword,
    skipBirthday,
    onBackClick,
    onNextClick,
    register = true,
    firstName: initialFirstName,
    lastName: initialLastName,
    birthday: initialBirthday = null,
  } = props;

  const materialStyles = useStyles();
  if (step !== currentStep && initialFocus) {
    setInitialFocus(false);
  } else if (step == currentStep && !initialFocus && firstNameRef && passwordRef) {
    setInitialFocus(true);
    setTimeout(() => {
      if (step == currentStep) {
        if (!skipName) {
          firstNameRef && firstNameRef.focus();
        } else {
          passwordRef && passwordRef.focus();
        }
      }
    }, 300);
  }

  const validate = () => {
    let error = false;
    let focus = false;
    if (!skipName) {
      if (
        (!firstName && (!initialFirstName || initialFirstName.length == 0)) ||
        (firstName && firstName.length == 0)
      ) {
        error = true;
        setFirstNameError(t('update-account.error_first_name_empty'));
        if (!focus) {
          focus = true;
          firstNameRef && firstNameRef.focus();
        }
      }
      if (
        (!lastName && (!initialLastName || initialLastName.length == 0)) ||
        (lastName && lastName.length == 0)
      ) {
        error = true;
        setLastNameError(t('update-account.error_last_name_empty'));
        if (!focus) {
          focus = true;
          lastNameRef && lastNameRef.focus();
        }
      }
    }

    if (!skipBirthday && !birthday && !initialBirthday) {
      error = true;
      setBirthdayError(t('update-account.error_birthday_empty'));
    }
    if (!skipPassword) {
      if (!password) {
        error = true;
        setPasswordError(t('update-account.error_password_empty'));
        if (!focus) {
          focus = true;
          passwordRef && passwordRef.focus();
        }
      } else if (password.length < 4) {
        error = true;
        setPasswordError(t('update-account.error_password_to_short'));
        if (!focus) {
          focus = true;
          passwordRef && passwordRef.focus();
        }
      }

      if (confirmPassword !== password) {
        error = true;
        setConfirmPasswordError(t('update-account.error_password_do_not_match'));
        if (!focus) {
          focus = true;
          confirmPasswordRef && confirmPasswordRef.focus();
        }
      }
    }
    return !error;
  };
  return (
    <Step className="stepstyles" number={step} current={currentStep}>
      {/* Scrollable container */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: 'calc(100% - 80px)',
          overflowX: 'hidden',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {
          <MediaQuery minHeight={410}>
            <div className={clsx(styles.logo, 'logo', 'update-logo')} />
          </MediaQuery>
        }

        {/* Text */}
        <div className={clsx(styles.header1, 'header1', 'update-header1')}>
          {register ? t('register-account.text') : t('update-account.text')}
        </div>
        <div className={clsx(styles.header2, 'header2', 'update-header2')}>
          {register ? t('register-account.subtext') : t('update-account.subtext')}
        </div>

        {/* Name field */}
        {!skipName && (
          <div className={clsx(styles.input, 'input', 'update-input-firstname')}>
            <Input
              ref={(element) => setFirstNameRef(element)}
              placeholder={t('update-account.placeholder_first_name')}
              value={firstName || initialFirstName}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && lastNameRef) {
                  lastNameRef.focus();
                }
              }}
              onChange={(e) => {
                setFirstName(e.target.value);
                setFirstNameError(undefined);
              }}
              style={{ width: '80%' }}
              helperText={firstNameError}
            />
          </div>
        )}
        {!skipName && (
          <div className={clsx(styles.input, 'input', 'update-input-lastname')}>
            <Input
              ref={(element) => setLastNameRef(element)}
              placeholder={t('update-account.placeholder_last_name')}
              value={lastName || initialLastName}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && passwordRef) {
                  passwordRef.focus();
                }
              }}
              onChange={(e) => {
                setLastName(e.target.value);
                setLastNameError(undefined);
              }}
              style={{ width: '80%' }}
              helperText={lastNameError}
            />
          </div>
        )}

        {!skipBirthday && (
          <div className={clsx(styles.input, 'input', 'update-input-birthday')}>
            <div style={{ width: '80%' }}>
              <MuiPickersUtilsProvider utils={dayjsUtils}>
                <DatePicker
                  className={clsx(styles.birthday, 'birthday')}
                  style={{ width: '100%' }}
                  value={birthday || initialBirthday}
                  disableFuture
                  openTo="year"
                  format="DD MMMM YYYY"
                  onChange={(v) => {
                    setBirthdayError(null);
                    setBirthday(v);
                  }}
                  emptyLabel={t('update-account.placeholder_birthday')}
                />
              </MuiPickersUtilsProvider>
              {birthdayError && (
                <div style={{ paddingLeft: 5, marginTop: 10, fontSize: 12, color: '#333' }}>
                  {birthdayError}
                </div>
              )}
            </div>
          </div>
        )}
        {/* Password fields */}
        {!skipPassword && (
          <div className={clsx(styles.input, 'input', 'update-input-password')}>
            <Input
              ref={(element) => setPasswordRef(element)}
              type="password"
              placeholder={t('update-account.placeholder_password')}
              value={password}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && confirmPasswordRef) {
                  confirmPasswordRef.focus();
                }
              }}
              onChange={(e) => {
                setPasswordError(undefined);
                setPassword(e.target.value);
              }}
              style={{ width: '80%' }}
              helperText={passwordError}
            />
          </div>
        )}
        {!skipPassword && (
          <div className={clsx(styles.input, 'input', 'update-input-password-confirm')}>
            <Input
              ref={(element) => {
                setConfirmPasswordRef(element);
              }}
              type="password"
              placeholder={t('update-account.placeholder_repeat_password')}
              value={confirmPassword}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (validate()) {
                    onNextClick({
                      firstName,
                      lastName,
                      password,
                      confirmPassword,
                      birthday,
                    });
                  }
                }
              }}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setConfirmPasswordError(undefined);
              }}
              style={{ width: '80%' }}
              helperText={confirmPasswordError}
            />
          </div>
        )}
      </div>

      {/* Buttons */}
      <ActionFooter
        backText={register ? t('register-account.back_button') : t('update-account.back_button')}
        nextText={register ? t('register-account.next_button') : t('update-account.next_button')}
        onNextClick={() => {
          if (validate()) {
            onNextClick({
              firstName,
              lastName,
              password,
              confirmPassword,
              birthday,
            });
          }
        }}
        onBackClick={() => onBackClick()}
      />
    </Step>
  );
}
